// frontend/src/pages/MatchScoring/index.js
//
// Live scoring console — the page mounted at /matches/:matchId/score.
//
// State machine (driven by /state response):
//   pre_innings_1  → no innings exists → PreInningsScreen for innings 1
//   live_1         → innings 1 status='live' → main scoring view
//   break          → innings 1 completed, innings 2 not started → InningsBreakScreen
//   pre_innings_2  → after break confirmation → PreInningsScreen with target_runs preset
//   live_2         → innings 2 status='live' → main scoring view
//   completed      → all innings completed → MatchEndScreen
//
// Ball submission flow:
//   user taps run/extra/wicket  →
//     (build pendingDelivery) →
//     (open modal if needed) →
//     (collect modal data) →
//     (build /balls payload) →
//     POST → on success, refresh state.
//
// We use uuid v4 for client_event_id (offline-queue dedupe). Even though
// we're online-only for v1, the dedupe protects against double-clicks
// and lost-network-but-success-actually scenarios.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

import useMatchState, { useNowTick } from './useMatchState';
import ScoringHeader from './ScoringHeader';
import ScoreBlock from './ScoreBlock';
import PlayersStrip from './PlayersStrip';
import BallsStrip from './BallsStrip';
import ScoringPad from './ScoringPad';
import ExtrasModal from './ExtrasModal';
import WicketModal from './WicketModal';
import WagonWheelModal from './WagonWheelModal';
import { composeCommentary } from './commentary';
import BowlerChangeModal from './BowlerChangeModal';
import { confirmCondition } from './conditions';
import PreInningsScreen from './PreInningsScreen';
import { InningsBreakScreen, MatchEndScreen } from './InningsBreakScreen';
import EditBallModal from './EditBallModal';
import InningsEndModal from './InningsEndModal';

import './MatchScoring.css';

// ── Helpers ───────────────────────────────────────────────
function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// ── Component ─────────────────────────────────────────────
// The Sanjha Billa plays for BOTH sides, so he is listed in each side. His
// row's `side` is only the side he is keeping for (if any), so his keeper flag
// counts on that side only.
function squadForSide(squad, side) {
  return (squad || [])
    .filter(s => s.side === side || s.is_saanjha)
    .map(s => (s.is_saanjha ? { ...s, is_keeper: !!s.is_keeper && s.side === side } : s));
}

export default function MatchScoringPage() {
  const { matchId } = useParams();
  const navigate    = useNavigate();
  const { isAdmin, member } = useAuth() || {};
  const isScorer = member?.role === 'scorer';
  // Everyone who can score can also correct: change bowler, swap strike, edit
  // a ball, keeper, end innings. The server has always allowed scorers all of
  // these; only this screen hid them, so on 4 Oct the scorer at the ground had
  // nothing but Undo to fix a wrong bowler with.
  const canScore = !!(isAdmin || isScorer);

  // Live state polling
  const { state, error, loaded, refresh, derived } = useMatchState(matchId);

  // Push 2 (Session 21 — 26 May 2026): 1-Hz clock for live time displays
  // (innings elapsed, batter time at crease). Pauses while tab is hidden.
  const now = useNowTick();

  // Setup-state for squad / team names — we need this once on mount
  const [setup, setSetup] = useState(null);
  const [setupErr, setSetupErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/scorer/matches/${matchId}/setup-state`);
        if (cancelled) return;
        setSetup(r.data);
      } catch (e) {
        if (cancelled) return;
        setSetupErr(e?.response?.data?.error || e?.message || 'setup load failed');
      }
    })();
    return () => { cancelled = true; };
  }, [matchId]);

  // UI state
  const [detailMode, setDetailMode]       = useState(true);
  const [pendingDelivery, setPending]     = useState(null);  // in-flight delivery object
  const [showExtras, setShowExtras]       = useState(null);  // 'wd'|'nb'|'b'|'lb'|null
  const [showWicket, setShowWicket]       = useState(false);
  const [showWheel, setShowWheel]         = useState(false);
  const [showBowlerChange, setShowBowlerChange] = useState(false);
  const [bowlerChangeForced, setBowlerChangeForced] = useState(false);  // true when end-of-over (no Cancel)
  const [showKeeper, setShowKeeper]       = useState(false);  // mid-innings keeper change
  // Penalty-runs modal
  const [showPenalty, setShowPenalty]     = useState(false);
  const [penaltyRuns, setPenaltyRuns]     = useState(5);
  const [penaltyReason, setPenaltyReason] = useState('');
  // Substitute-fielder modal
  const [showSub, setShowSub]             = useState(false);
  const [subSide, setSubSide]             = useState('A');
  const [subMemberId, setSubMemberId]     = useState('');
  const [subCanKeep, setSubCanKeep]       = useState(false);
  // Arbitrary runs picker (overthrows producing 5, 7, etc. off a single ball)
  const [showRunPicker, setShowRunPicker] = useState(false);
  const [postBusy, setPostBusy]           = useState(false);
  const [postErr, setPostErr]             = useState(null);
  const [toast, setToast]                 = useState(null);
  const [confirmInningsBreak, setConfirmInningsBreak] = useState(false);

  // Edit-ball modal state
  const [editingBall, setEditingBall]     = useState(null);  // ball row being edited; null = closed
  const [editBusy, setEditBusy]           = useState(false);

  // "Edit any ball" browser (loads ALL balls of the match, not just recent)
  const [showBallBrowser, setShowBallBrowser] = useState(false);
  const [allBalls, setAllBalls]               = useState(null);  // null = not loaded
  const [ballBrowserBusy, setBallBrowserBusy] = useState(false);

  // Manual innings-end modal
  const [showInningsEnd, setShowInningsEnd] = useState(false);
  const [inningsEndBusy, setInningsEndBusy] = useState(false);

  // Members by id (combined: setup.present_members + setup.squad members +
  // any substitute fielders, so a sub who is NOT in the playing XI still
  // resolves to a display name in the fielder pickers).
  const membersById = useMemo(() => {
    const m = new Map();
    (setup?.present_members || []).forEach(p => m.set(p.id, p));
    (state?.substitutes || []).forEach(s => {
      if (!m.has(s.member_id)) m.set(s.member_id, { id: s.member_id, display_name: s.display_name });
    });
    return m;
  }, [setup, state]);

  // Squad split by side
  const squadByMember = useMemo(() => {
    const m = new Map();
    (setup?.squad || []).forEach(s => m.set(s.member_id, s));
    return m;
  }, [setup]);
  // Sanjha Billa appears in both sides (see squadForSide).
  const squadA = useMemo(() => squadForSide(setup?.squad, 'A'), [setup]);
  const squadB = useMemo(() => squadForSide(setup?.squad, 'B'), [setup]);

  // Substitute fielders split by side, shaped like squad rows (member_id +
  // is_sub flag + can_keep). These get merged into the FIELDER pickers only —
  // a sub can take a catch / effect a run-out, but never bats, bowls, or keeps
  // unless can_keep is set.
  const subsA = useMemo(() =>
    (state?.substitutes || []).filter(s => s.side === 'A')
      .map(s => ({ member_id: s.member_id, is_sub: true, can_keep: !!s.can_keep })),
    [state]);
  const subsB = useMemo(() =>
    (state?.substitutes || []).filter(s => s.side === 'B')
      .map(s => ({ member_id: s.member_id, is_sub: true, can_keep: !!s.can_keep })),
    [state]);
  const teamAName = setup?.captaincy?.capt1_team || 'Side A';
  const teamBName = setup?.captaincy?.capt2_team || 'Side B';
  const maxOvers  = setup?.match?.overs || 30;

  // Toss decision determines who bats first (innings 1).
  // By convention (see scorer.js squads insert): capt1 = Side A, capt2 = Side B.
  // toss_decision is 'bat' | 'bowl'. winner_id is the captain who won the toss.
  function inferInningsOneSides() {
    const cap = setup?.captaincy;
    if (!cap || !cap.winner_id) return { battingSide: 'A', bowlingSide: 'B' };
    const winnerSide = (cap.winner_id === cap.capt1_id) ? 'A' : 'B';
    const decision   = cap.toss_decision; // 'bat' | 'bowl'
    const battingSide = (decision === 'bat') ? winnerSide : (winnerSide === 'A' ? 'B' : 'A');
    const bowlingSide = battingSide === 'A' ? 'B' : 'A';
    return { battingSide, bowlingSide };
  }

  // ── Determine current stage from /state ────────────────
  const allInnings = state?.innings || [];
  const inn1 = allInnings.find(i => i.innings_number === 1);
  const inn2 = allInnings.find(i => i.innings_number === 2);
  const currentInnings  = derived?.currentInnings || null;
  const liveStateForCur = derived?.liveStateForCurrent || null;
  const recentForCur    = (state?.recent_balls || []).filter(b => currentInnings && b.innings_id === currentInnings.id);
  // The over in progress, for the bowler-change modal: how many deliveries it
  // already has, who bowled them, and who bowled the over before. recent_balls
  // carries every active ball of the live innings, in sequence order.
  const overNow           = Math.floor((parseInt(currentInnings?.legal_balls, 10) || 0) / 6);
  const ballsThisOver     = recentForCur.filter(b => Number(b.over_number) === overNow);
  // "No bowling" / "No keeping" given for this match, the bowling limit for the
  // innings in progress, and overs each bowler has bowled in it (migration 053).
  const conditionsById = new Map((state?.conditions || []).map(c => [c.member_id, c]));
  const curLimit = currentInnings ? (state?.bowling_limits || []).find(l => l.innings_id === currentInnings.id) : null;
  const oversBy = new Map((state?.bowler_overs || [])
    .filter(r => currentInnings && r.innings_id === currentInnings.id)
    .map(r => [r.bowler_id, Number(r.overs)]));
  const overBowlerId      = ballsThisOver.length ? ballsThisOver[ballsThisOver.length - 1].bowler_id : null;
  const lastOverBalls     = recentForCur.filter(b => Number(b.over_number) === overNow - 1);
  const lastOverBowlerId  = lastOverBalls.length ? lastOverBalls[lastOverBalls.length - 1].bowler_id : null;
  // Retired-hurt batsmen for the current innings — eligible to be brought back.
  const retiredHurtForCur = (state?.retired_hurt || []).filter(r => currentInnings && r.innings_id === currentInnings.id);

  let stage;
  if (!loaded || !setup) stage = 'loading';
  else if (!inn1)                              stage = 'pre_innings_1';
  else if (inn1.status === 'live')             stage = 'live_1';
  else if (inn1.status === 'completed' && !inn2) stage = (confirmInningsBreak ? 'pre_innings_2' : 'break');
  else if (inn2 && inn2.status === 'live')     stage = 'live_2';
  else if (inn2 && inn2.status === 'completed') stage = 'completed';
  else                                          stage = 'pre_innings_1';

  // ── Already-out tracking for next-batter pickers ──────
  // The /state endpoint returns `dismissed_balls` covering ALL active balls
  // with a dismissal in this match (not just the last 12), so the next-batter
  // picker correctly excludes batters who fell earlier in the innings.
  const alreadyOut = useMemo(() => {
    const set = new Set();
    if (!currentInnings) return set;
    (state?.dismissed_balls || []).forEach(b => {
      if (b.innings_id === currentInnings.id && b.dismissed_player_id) {
        set.add(b.dismissed_player_id);
      }
    });
    return set;
  }, [state, currentInnings]);

  // ── Pad handlers ──────────────────────────────────────
  function handleRun(runs) {
    if (!liveStateForCur) return;
    const delivery = {
      kind: 'bat',
      runs,
      isLegal: true,
      isFreeHit: !!liveStateForCur.pending_free_hit,
    };
    if (detailMode && runs > 0) {
      setPending(delivery);
      setShowWheel(true);
    } else {
      submitBall(delivery, {});
    }
  }

  function handleBoundary(runs) {
    if (!liveStateForCur) return;
    const delivery = {
      kind: 'bat',
      runs,
      isLegal: true,
      isBoundary: true,
      boundaryValue: runs,
      isFreeHit: !!liveStateForCur.pending_free_hit,
    };
    setPending(delivery);
    setShowWheel(true);
  }

  function handleExtra(kind) {
    setShowExtras(kind);
  }

  function handleWicket() {
    setShowWicket(true);
  }

  function handleUndo() {
    // For Sunday v1: void the most recent active ball via /balls/:id/void.
    // We need the last active ball id; recentBalls is sorted asc per innings,
    // so take last from recentForCur.
    const last = recentForCur[recentForCur.length - 1];
    if (!last) return;
    if (!window.confirm('Undo the last ball? This voids it; you can re-enter.')) return;
    (async () => {
      setPostBusy(true);
      try {
        await api.post(`/scorer/balls/${last.id}/void`, { reason: 'undo' });
        setToast({ kind: 'ok', text: 'Ball voided' });
        await refresh();
      } catch (e) {
        setToast({ kind: 'err', text: e?.response?.data?.error || 'Undo failed' });
      } finally {
        setPostBusy(false);
      }
    })();
  }

  // ── Edit-ball: tap a ball in the strip to open EditBallModal ──
  function handleBallTap(ball) {
    // ball is a recent-balls row; we have everything we need already.
    setEditingBall(ball);
  }

  // ── "Edit any ball" browser: fetch ALL balls of the match ──
  async function openBallBrowser() {
    setShowBallBrowser(true);
    setBallBrowserBusy(true);
    try {
      const r = await api.get(`/scorer/matches/${matchId}/all-balls`);
      setAllBalls(r.data?.balls || []);
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.error || 'Could not load balls' });
      setAllBalls([]);
    } finally {
      setBallBrowserBusy(false);
    }
  }
  function openBallFromBrowser(ball) {
    setShowBallBrowser(false);
    setToast({ kind: 'ok', text: `Editing ${ball.display_label || `${ball.over_number}.${ball.ball_in_over}`}` });
    // Defer to the next tick so the tap that closed the browser doesn't also
    // land on the editor's backdrop and immediately dismiss it.
    setTimeout(() => setEditingBall(ball), 0);
  }

  async function onEditSave(payload) {
    if (!editingBall) return;
    setEditBusy(true);
    try {
      await api.patch(`/scorer/balls/${editingBall.id}`, payload);
      setToast({ kind: 'ok', text: 'Ball updated' });
      setEditingBall(null);
      await refresh();
    } catch (e) {
      const data = e?.response?.data || {};
      const msg = data.detail || data.error || e?.message || 'Edit failed';
      console.error('[edit-ball] failed:', msg, data);
      setToast({ kind: 'err', text: `Edit failed: ${msg}` });
    } finally {
      setEditBusy(false);
    }
  }

  // ── Manual innings-end ──
  async function onInningsEndConfirm(reason) {
    if (!currentInnings) return;
    setInningsEndBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/innings/${currentInnings.id}/end`, { end_reason: reason });
      setToast({ kind: 'ok', text: `Innings ${currentInnings.innings_number} ended` });
      setShowInningsEnd(false);
      await refresh();
    } catch (e) {
      const data = e?.response?.data || {};
      const msg = data.detail || data.error || e?.message || 'End-innings failed';
      console.error('[innings-end] failed:', msg, data);
      setToast({ kind: 'err', text: `End failed: ${msg}` });
    } finally {
      setInningsEndBusy(false);
    }
  }

  // ── Modal callbacks ───────────────────────────────────
  function onExtraConfirm(extra) {
    setShowExtras(null);
    // If the extra produced a directional play (bye/lb/wide-with-bye-or-lb-runs/nb-with-runs),
    // chain to wagon-wheel (when detailMode on AND there's actually direction).
    let delivery;
    if (extra.kind === 'wd') {
      delivery = {
        kind: 'wd',
        runs: extra.runs,
        wideUnderlying: extra.wideUnderlying,
        wicketKind: extra.wicketKind,
        isLegal: false,
      };
    } else if (extra.kind === 'nb') {
      const nbBat = extra.nbSource === 'bat' ? extra.nbExtraRuns : 0;
      delivery = {
        kind: 'nb',
        offTheBat: nbBat,
        nbSource:  extra.nbSource,
        nbExtraRuns: extra.nbExtraRuns,
        // A 4 or 6 hit off a no-ball is still a boundary for the batter — flag it
        // so fours/sixes counts and boundary stats are correct (only when the runs
        // came off the bat, not byes/leg-byes off the no-ball).
        isBoundary: nbBat === 4 || nbBat === 6,
        boundaryValue: (nbBat === 4 || nbBat === 6) ? nbBat : undefined,
        wicketKind: extra.wicketKind,
        isLegal: false,
        triggersFreeHit: true,
      };
    } else {
      delivery = {
        kind: extra.kind,           // 'b' | 'lb'
        runs: extra.runs,
        wicketKind: extra.wicketKind,
        isLegal: true,
      };
    }

    const hasDirection =
      (delivery.kind === 'wd' && delivery.runs >= 2 && (delivery.wideUnderlying === 'bye' || delivery.wideUnderlying === 'legbye')) ||
      (delivery.kind === 'nb' && (delivery.nbExtraRuns > 0)) ||
      (delivery.kind === 'b'  && delivery.runs > 0) ||
      (delivery.kind === 'lb' && delivery.runs > 0);

    if (detailMode && hasDirection && delivery.wicketKind == null) {
      setPending(delivery);
      setShowWheel(true);
    } else {
      submitBall(delivery, {});
    }
  }

  function onWicketConfirm(w) {
    setShowWicket(false);

    // Retired hurt is NOT a wicket and NOT a delivery — it's a status change.
    // Route it to the retire-hurt endpoint (the batsman keeps their score and
    // may return later via the "bring back" control), then refresh.
    if (w.isRetiredHurt) {
      (async () => {
        try {
          await api.post(`/scorer/matches/${matchId}/retire-hurt`, {
            innings_id: currentInnings.id,
            member_id: w.dismissedPlayerId,
            replacement_id: w.replacementId || null,
          });
          setToast({ kind: 'ok', text: 'Batsman retired hurt — keeps their score, can return later.' });
          refresh();
        } catch (e) {
          setToast({ kind: 'err', text: e?.response?.data?.error || 'Retire hurt failed' });
        }
      })();
      return;
    }

    // A wicket entered from the W button is a legal ball. For a run-out, the
    // batsmen may have completed runs before the dismissal — carry them onto
    // the ball so the total, the batsman's score, and strike rotation are all
    // correct (the backend handles the crossing from the completed-run parity).
    const roRuns = w.dismissalType === 'run_out' ? (w.runOutRuns || 0) : 0;
    const delivery = {
      kind: 'wkt',
      runs: roRuns,
      runOutRunType: w.runOutRunType || 'bat',
      isLegal: true,
      dismissal: w,                     // payload from WicketModal
      nextBatterId: w.nextBatterId,
    };
    // Caught and run-out have a meaningful shot direction. Route them through
    // the wagon wheel (the same modal a normal scoring shot uses) so the zone
    // is captured and rides along into this same ball. Other dismissal types
    // (bowled, lbw, stumped, hit wicket, retired) have no shot direction, so
    // submit immediately. onWheelConfirm/onWheelSkip already submit
    // pendingDelivery — which here is this wicket delivery — with/without the
    // zone detail, so the dismissal + zone land on one ball.
    if (w.dismissalType === 'caught' || w.dismissalType === 'run_out') {
      setPending(delivery);
      setShowWheel(true);
    } else {
      submitBall(delivery, {});
    }
  }

  function onWheelConfirm(detail) {
    setShowWheel(false);
    submitBall(pendingDelivery, detail);
    setPending(null);
  }
  function onWheelSkip() {
    setShowWheel(false);
    submitBall(pendingDelivery, {});
    setPending(null);
  }

  // ── Build /balls payload + POST ───────────────────────
  async function submitBall(delivery, detail) {
    if (!currentInnings || !liveStateForCur) {
      setToast({ kind: 'err', text: 'No live innings' });
      return;
    }
    const inn  = currentInnings;
    const live = liveStateForCur;

    // Resolve the striker defensively. In last-man-stands, an earlier state hiccup
    // can leave live.striker_id null/invalid (e.g. a dummy slot promoted on strike).
    // For a wicket the person who got out IS the striker who faced the ball, so fall
    // back to the dismissed player's id — this is always set by the WicketModal and
    // guarantees the final wicket saves instead of failing "striker_id required".
    const resolvedStrikerId =
      live.striker_id ||
      (delivery.kind === 'wkt' ? (delivery.dismissal?.dismissedPlayerId || null) : null);

    // Guard: a non-wicket ball with no striker on strike means the crease is in a
    // bad state (e.g. last man already out). Don't fire a doomed request — tell the
    // scorer what to do. A wicket delivery is allowed through via the fallback above.
    if (!resolvedStrikerId && delivery.kind !== 'wkt') {
      setToast({ kind: 'err', text: 'No batter on strike — end the innings or set the striker first.' });
      return;
    }
    if (!live.bowler_id) {
      setToast({ kind: 'err', text: 'No bowler set — pick the bowler before scoring.' });
      return;
    }

    // Map delivery + detail → payload
    const payload = {
      client_event_id: uuidv4(),
      innings_id:      inn.id,
      striker_id:      resolvedStrikerId,
      non_striker_id:  live.non_striker_id || null,
      bowler_id:       live.bowler_id,
      is_legal:        delivery.isLegal !== false,
      is_free_hit:     !!live.pending_free_hit,
      triggers_free_hit: !!delivery.triggersFreeHit,

      runs_off_bat: 0,
      wides:        0,
      noballs:      0,
      byes:         0,
      legbyes:      0,

      is_boundary:    !!delivery.isBoundary,
      boundary_value: delivery.boundaryValue ?? null,

      wide_underlying: null,
      noball_source:   null,

      zone_sector: detail?.zoneSector ?? null,
      zone_ring:   detail?.zoneRing   ?? null,
      shot_type:   detail?.shotType   ?? null,
      fielder_id:  detail?.fielderId  ?? null,
      incident:    detail?.incident   ?? null,
      incident_player_id: detail?.fielderId ?? null,

      dismissal_type:        null,
      dismissed_player_id:   null,
      dismissal_fielder_id:  null,
      dismissal_fielder2_id: null,

      drs_taken_by:           null,
      drs_original_decision:  null,
      drs_decision:           null,

      commentary_text:        detail?.commentaryText ?? null,
      commentary_overridden:  !!detail?.commentaryOverridden,
    };

    // Map kind → numeric atoms
    if (delivery.kind === 'bat') {
      payload.runs_off_bat = delivery.runs || 0;
    } else if (delivery.kind === 'wd') {
      payload.wides = delivery.runs;
      payload.wide_underlying = delivery.wideUnderlying || null;
    } else if (delivery.kind === 'nb') {
      payload.noballs = 1;
      payload.noball_source = delivery.nbSource || 'none';
      const r = delivery.nbExtraRuns || 0;
      if (delivery.nbSource === 'bat')         payload.runs_off_bat = r;
      else if (delivery.nbSource === 'bye')    payload.byes    = r;
      else if (delivery.nbSource === 'legbye') payload.legbyes = r;
    } else if (delivery.kind === 'b') {
      payload.byes = delivery.runs;
    } else if (delivery.kind === 'lb') {
      payload.legbyes = delivery.runs;
    } else if (delivery.kind === 'wkt') {
      const w = delivery.dismissal;
      // Run-out completed runs before the dismissal: off the bat (credited to
      // the striker) by default, or byes/leg-byes if the run wasn't off the bat.
      const roRuns = (w.dismissalType === 'run_out') ? (delivery.runs || 0) : 0;
      if (roRuns > 0) {
        if (delivery.runOutRunType === 'bye')         payload.byes         = roRuns;
        else if (delivery.runOutRunType === 'legbye') payload.legbyes      = roRuns;
        else                                          payload.runs_off_bat = roRuns;
      }
      payload.dismissal_type        = w.dismissalType;
      payload.dismissed_player_id   = w.dismissedPlayerId;
      payload.dismissal_fielder_id  = w.fielderId  || null;
      payload.dismissal_fielder2_id = w.fielder2Id || null;
      // Next batter is resolved atomically by the backend (replaces the
      // dismissed player's crease slot, then rotates). No separate call.
      payload.next_batter_id        = w.nextBatterId || null;
      payload.drs_taken_by          = w.drsTakenBy        || null;
      payload.drs_original_decision = w.drsOriginalDecision || null;
      payload.drs_decision          = w.drsDecision       || null;

      // The wagon wheel (used for caught/run-out) builds its commentary as a
      // normal shot with no dismissal context, so it emits "Dot ball" AND hands
      // it back flagged commentary_overridden=true. So we rebuild the wicket
      // line UNCONDITIONALLY from the dismissal — the wheel can never produce
      // correct dismissal text. A keeper catch is labelled "caught behind".
      {
        const bSide    = derived?.currentInnings?.bowling_side;
        const bSquad   = bSide === 'A' ? squadA : squadB;
        const keeperRow = bSquad?.find(s => s.is_keeper);
        const isCaughtBehind = w.dismissalType === 'caught_behind'
          || (w.dismissalType === 'caught'
              && keeperRow && w.fielderId && keeperRow.member_id === w.fielderId);
        payload.commentary_text = composeCommentary({
          kind: 'wkt',
          runs: 0,
          zoneSector: detail?.zoneSector ?? null,
          zoneRing:   detail?.zoneRing   ?? null,
          zoneSideHint: detail?.zoneSideHint ?? null,
          shotType:   detail?.shotType   ?? null,
          fielderName: w.fielderId ? (membersById.get(w.fielderId)?.display_name || '') : null,
          batterName:  membersById.get(w.dismissedPlayerId)?.display_name || '',
          bowlerName:  live.bowler_id ? (membersById.get(live.bowler_id)?.display_name || '') : '',
          dismissalType: isCaughtBehind ? 'caught behind' : w.dismissalType,
        });
        // Note the completed runs on a run-out so the line explains the score change.
        if (roRuns > 0) {
          payload.commentary_text += ` (${roRuns} run${roRuns === 1 ? '' : 's'} completed)`;
        }
        // Mark as auto so the rebuilt line is treated as canonical, not a manual edit.
        payload.commentary_overridden = false;
      }
    }

    // Inline wicket on extras (stumping/run-out on wide/nb/etc.)
    if (delivery.wicketKind) {
      // The dismissed player is the striker by default; for stumped/run-out the
      // captain may want to record the opposite. For v1 we record striker out
      // and let captain edit afterwards if needed.
      payload.dismissal_type = delivery.wicketKind;
      payload.dismissed_player_id = live.striker_id;
    }

    setPostBusy(true); setPostErr(null);
    try {
      const res = await api.post(`/scorer/matches/${matchId}/balls`, payload);
      // Idempotency note: client_event_id ensures retries don't double-record.
      if (!res.data?.ok) throw new Error('Server returned non-ok');

      await refresh();
      setToast({ kind: 'ok', text: delivery.kind === 'wkt' ? 'Wicket recorded' : 'Ball saved' });

      // Next-batter promotion is now handled atomically by the backend ball
      // insert (it replaces the dismissed player's crease slot, then rotates).
      // The old client-side /live-state promotion call was removed: it raced
      // with the backend's strike rotation and dismissed the wrong batter when
      // a wicket fell on the last ball of an over.

      // After a legal ball that completes an over: if the overs limit has
      // been reached, end the innings automatically; otherwise prompt for the
      // next bowler.
      const newLegalBalls = (parseInt(currentInnings.legal_balls, 10) || 0) + (payload.is_legal ? 1 : 0);
      const overJustCompleted = payload.is_legal && newLegalBalls % 6 === 0;
      if (overJustCompleted && (newLegalBalls / 6) >= maxOvers) {
        try {
          await api.post(`/scorer/matches/${matchId}/innings/${currentInnings.id}/end`, { end_reason: 'overs_done' });
          setToast({ kind: 'ok', text: `Innings ended — ${maxOvers} overs complete` });
          await refresh();
        } catch (e) {
          const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'auto end-innings failed';
          setToast({ kind: 'err', text: `Auto-end failed: ${msg}` });
        }
      } else if (overJustCompleted) {
        setBowlerChangeForced(true);
        setShowBowlerChange(true);
      }
    } catch (e) {
      setPostErr(e?.response?.data?.error || e?.message || 'Save failed');
      setToast({ kind: 'err', text: e?.response?.data?.error || 'Save failed' });
      // Server refused a ball by a different bowler mid-over: open Change
      // bowler so the scorer corrects the over or declares the change.
      if (e?.response?.data?.code === 'BOWLER_MID_OVER') {
        await refresh();
        setBowlerChangeForced(false);
        setShowBowlerChange(true);
      }
    } finally {
      setPostBusy(false);
    }
  }

  // ── Toast auto-dismiss ────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Render ────────────────────────────────────────────
  if (stage === 'loading') {
    return <div className="sc-root"><div className="sc-loading">Loading scoring console…</div></div>;
  }
  if (setupErr || error) {
    return <div className="sc-root"><div className="sc-error-box">{setupErr || error}</div></div>;
  }

  // ── Captaincy gate (scorer only) ──────────────────────────
  // A scorer cannot reach the scoring UI until BOTH captains are assigned by
  // an admin. The backend enforces this too (requireCaptainsSet on
  // /innings/start, /balls, etc), but this gives the scorer a clean lock
  // screen instead of letting them load the openers form and 403 on submit.
  // Admins/super-admins pass through (they may be mid-setup).
  {
    const cap = setup?.captaincy;
    const captainsSet = cap && cap.capt1_id && cap.capt2_id;
    if (isScorer && !captainsSet) {
      return (
        <div className="sc-root">
          <div className="sc-error-box" style={{ textAlign: 'center', maxWidth: 420, margin: '40px auto' }}>
            <h2 style={{ color: '#1E3A6E', marginBottom: 8 }}>Captains not finalized yet</h2>
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              An admin needs to finalize captains for this match before scoring can begin.
            </p>
            <button
              className="sc-btn"
              style={{ marginTop: 12 }}
              onClick={() => navigate('/')}
            >
              Back to matches
            </button>
          </div>
        </div>
      );
    }
  }

  // Header + stage body
  let body = null;

  if (stage === 'pre_innings_1') {
    const { battingSide, bowlingSide } = inferInningsOneSides();
    const battingSquad = battingSide === 'A' ? squadA : squadB;
    const bowlingSquad = bowlingSide === 'A' ? squadA : squadB;
    body = (
      <PreInningsScreen
        matchId={matchId}
        inningsNumber={1}
        battingSide={battingSide}
        bowlingSide={bowlingSide}
        maxOvers={maxOvers}
        targetRuns={null}
        battingSquad={battingSquad}
        bowlingSquad={bowlingSquad}
        membersById={membersById}
        conditionsById={conditionsById}
        onStarted={() => refresh()}
      />
    );
  } else if (stage === 'break') {
    body = (
      <InningsBreakScreen
        matchId={matchId}
        completedInnings={inn1}
        onStartNext={() => setConfirmInningsBreak(true)}
      />
    );
  } else if (stage === 'pre_innings_2') {
    // Innings 2: roles flip
    const battingSide = inn1.bowling_side;
    const bowlingSide = inn1.batting_side;
    const battingSquad = battingSide === 'A' ? squadA : squadB;
    const bowlingSquad = bowlingSide === 'A' ? squadA : squadB;
    const target = parseInt(inn1.total_runs, 10) + 1;
    body = (
      <PreInningsScreen
        matchId={matchId}
        inningsNumber={2}
        battingSide={battingSide}
        bowlingSide={bowlingSide}
        maxOvers={maxOvers}
        targetRuns={target}
        battingSquad={battingSquad}
        bowlingSquad={bowlingSquad}
        membersById={membersById}
        conditionsById={conditionsById}
        onStarted={() => { setConfirmInningsBreak(false); refresh(); }}
      />
    );
  } else if (stage === 'completed') {
    body = (
      <MatchEndScreen
        allInnings={allInnings}
        membersById={membersById}
        matchId={matchId}
        playersPerSide={setup?.match?.players_per_side}
        onComplete={() => navigate(`/scorecards/${matchId}`)}
      />
    );
  } else {
    // live_1 or live_2
    const battingSide  = currentInnings.batting_side;
    const bowlingSide  = currentInnings.bowling_side;
    const bowlingSquad = bowlingSide === 'A' ? squadA : squadB;
    const battingSquad = battingSide === 'A' ? squadA : squadB;
    // Fielder pickers (catch / run-out) also include any substitute fielders on
    // the fielding side. Bowler-change + keeper pickers keep the plain XI.
    const fieldingSquad = [...bowlingSquad, ...(bowlingSide === 'A' ? subsA : subsB)];
    const striker     = liveStateForCur?.striker_id     ? membersById.get(liveStateForCur.striker_id)     : null;
    const nonStriker  = liveStateForCur?.non_striker_id ? membersById.get(liveStateForCur.non_striker_id) : null;
    const bowler      = liveStateForCur?.bowler_id      ? membersById.get(liveStateForCur.bowler_id)      : null;

    body = (
      <>
        <ScoreBlock currentInnings={currentInnings} liveState={liveStateForCur} allInnings={allInnings} now={now} />
        <PlayersStrip
          liveState={liveStateForCur}
          recentBalls={recentForCur}
          membersById={membersById}
          drsByKey={derived?.drsByKey}
          batterFirstSeenById={derived?.batterFirstSeenById}
          now={now}
          onSwapStrike={async () => {
            if (!liveStateForCur || !currentInnings) return;
            try {
              await api.post(`/scorer/matches/${matchId}/live-state`, {
                innings_id:     currentInnings.id,
                striker_id:     liveStateForCur.non_striker_id,
                non_striker_id: liveStateForCur.striker_id,
              });
              await refresh();
              setToast({ kind: 'ok', text: 'Strike swapped' });
            } catch (e) {
              setToast({ kind: 'err', text: e?.response?.data?.error || 'Swap failed' });
            }
          }}
          onChangeNonStriker={() => setToast({ kind: 'err', text: 'Edit non-striker — TBD' })}
          onChangeBowler={() => { setBowlerChangeForced(false); setShowBowlerChange(true); }}
          canEdit={canScore}
        />
        {(() => {
          const kRow = bowlingSquad.find(s => s.is_keeper);
          const kName = kRow ? (membersById.get(kRow.member_id)?.display_name || kRow.member_id.slice(0, 8)) : '— none set —';
          return (
            <div className="sc-keeper-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px 8px', fontSize: 13 }}>
              <span style={{ color: 'var(--sc-text-mute, #888)' }}>
                🧤 Keeper: <strong style={{ color: kRow ? 'inherit' : '#b91c1c' }}>{kName}</strong>
              </span>
              {canScore && (
                <button className="sc-icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setShowKeeper(true)}>
                  Change
                </button>
              )}
            </div>
          );
        })()}
        {canScore && retiredHurtForCur.length > 0 && (
          <div className="sc-retired-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 12px 8px', fontSize: 13 }}>
            <span style={{ color: 'var(--sc-text-mute, #888)' }}>🏏 Retired hurt:</span>
            {retiredHurtForCur.map(r => (
              <button
                key={r.member_id}
                className="sc-icon-btn"
                title="Bring this batsman back to the crease"
                onClick={async () => {
                  // Determine which end is empty; default striker if both empty.
                  const ls = liveStateForCur || {};
                  let end = null;
                  if (!ls.striker_id) end = 'striker';
                  else if (!ls.non_striker_id) end = 'non_striker';
                  if (!end) {
                    setToast({ kind: 'err', text: 'Both ends are occupied — a batsman must be out or retired first.' });
                    return;
                  }
                  try {
                    await api.post(`/scorer/matches/${matchId}/return-to-bat`, {
                      innings_id: currentInnings.id,
                      member_id: r.member_id,
                      end,
                    });
                    setToast({ kind: 'ok', text: `${r.display_name} is back in.` });
                    refresh();
                  } catch (e) {
                    setToast({ kind: 'err', text: e?.response?.data?.error || 'Bring back failed' });
                  }
                }}
              >
                ↩ {r.display_name}
              </button>
            ))}
          </div>
        )}
        {/* The bowler on screen isn't the one bowling this over (e.g. after an
            Undo across an over change). Every ball would be refused, so say so
            plainly and offer the one-tap fix. */}
        {ballsThisOver.length > 0 && overBowlerId && liveStateForCur?.bowler_id && overBowlerId !== liveStateForCur.bowler_id && (
          <div style={{ margin: '6px 12px', padding: '10px 12px', borderRadius: 10, background: '#fef3c7',
                        border: '1px solid #f59e0b', color: '#92400e', fontSize: 13, display: 'flex',
                        alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200 }}>
              ⚠ This over is being bowled by <strong>{membersById.get(overBowlerId)?.display_name || 'another bowler'}</strong>,
              but the bowler is set to <strong>{membersById.get(liveStateForCur.bowler_id)?.display_name || '—'}</strong>.
            </span>
            <button className="sc-icon-btn" disabled={postBusy}
              onClick={async () => {
                try {
                  await api.post(`/scorer/matches/${matchId}/innings/${currentInnings.id}/correct-over-bowler`, {
                    over_number: overNow, to_bowler_id: overBowlerId,
                  });
                  await refresh();
                  setToast({ kind: 'ok', text: `Bowler set to ${membersById.get(overBowlerId)?.display_name || 'over bowler'}` });
                } catch (e) {
                  setToast({ kind: 'err', text: e?.response?.data?.error || 'Could not set the bowler' });
                }
              }}>
              Continue with {membersById.get(overBowlerId)?.display_name?.split(' ')[0] || 'him'}
            </button>
            <button className="sc-icon-btn" onClick={() => { setBowlerChangeForced(false); setShowBowlerChange(true); }}>
              Other…
            </button>
          </div>
        )}
        <BallsStrip
          recentBalls={recentForCur}
          membersById={membersById}
          onBallTap={canScore ? handleBallTap : null}
        />
        <ScoringPad
          detailMode={detailMode}
          onRun={handleRun}
          onRunOther={() => setShowRunPicker(true)}
          onBoundary={handleBoundary}
          onExtra={handleExtra}
          onWicket={handleWicket}
          onUndo={handleUndo}
          onToggleDetail={() => setDetailMode(!detailMode)}
          isFreeHit={!!liveStateForCur?.pending_free_hit}
          busy={postBusy}
        />
        <div className="sc-bottom">
          <span className={`sc-save ${postBusy ? 'sc-pending' : ''} ${postErr ? 'sc-error' : ''}`}>
            {postBusy ? 'Saving…' : postErr ? 'Save failed' : 'Synced'}
          </span>
          {canScore && (
            <button
              className="sc-icon-btn"
              onClick={openBallBrowser}
              title="Edit any ball in the match"
            >
              ✎ Edit any ball
            </button>
          )}
          {canScore && currentInnings && (
            <button
              className="sc-icon-btn"
              onClick={() => setShowPenalty(true)}
              title="Award penalty runs (dead ball — not charged to batsman or bowler)"
            >
              ⚑ Penalty
            </button>
          )}
          {canScore && (
            <button
              className="sc-icon-btn"
              onClick={() => setShowSub(true)}
              title="Add a substitute fielder"
            >
              ⇄ Sub fielder
            </button>
          )}
          {canScore && currentInnings && (
            <button
              className="sc-icon-btn"
              onClick={() => setShowInningsEnd(true)}
              title="End the current innings"
            >
              End innings ▸
            </button>
          )}
        </div>

        {showPenalty && currentInnings && (
          <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowPenalty(false)}>
            <div className="sc-modal">
              <div className="sc-modal-body">
                <h3>Penalty runs</h3>
                <div style={{ fontSize: 12, color: 'var(--sc-text-mute, #888)', marginBottom: 10 }}>
                  Added to the batting side's total. Not a delivery — not counted to any batsman's
                  balls or the bowler's over.
                </div>
                <h4>Runs</h4>
                <div className="sc-chip-row">
                  {[5, 1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      className={`sc-chip ${penaltyRuns === n ? 'sc-active' : ''}`}
                      onClick={() => setPenaltyRuns(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <h4>Reason (optional)</h4>
                <div className="sc-chip-row">
                  {['helmet/keeper', 'fielding offence', 'short run', 'other'].map(r => (
                    <button
                      key={r}
                      className={`sc-chip ${penaltyReason === r ? 'sc-active' : ''}`}
                      onClick={() => setPenaltyReason(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <div className="sc-modal-actions" style={{ marginTop: 14 }}>
                  <button className="sc-cancel" onClick={() => setShowPenalty(false)}>Cancel</button>
                  <button
                    className="sc-confirm"
                    onClick={async () => {
                      try {
                        await api.post(`/scorer/matches/${matchId}/penalty`, {
                          innings_id: currentInnings.id,
                          runs: penaltyRuns,
                          reason: penaltyReason || null,
                        });
                        setToast({ kind: 'ok', text: `${penaltyRuns} penalty runs added.` });
                        setShowPenalty(false);
                        refresh();
                      } catch (e) {
                        setToast({ kind: 'err', text: e?.response?.data?.error || 'Penalty failed' });
                      }
                    }}
                  >
                    Add {penaltyRuns} runs
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRunPicker && (
          <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowRunPicker(false)}>
            <div className="sc-modal">
              <div className="sc-modal-body">
                <h3>Runs off the bat</h3>
                <div style={{ fontSize: 12, color: 'var(--sc-text-mute, #888)', marginBottom: 10 }}>
                  For overthrows or unusual totals off a single ball (e.g. ran 1 + 4 overthrows = 5).
                  These are credited to the batter as runs off the bat.
                </div>
                <div className="sc-chip-row">
                  {[5, 7, 8, 9].map(n => (
                    <button
                      key={n}
                      className="sc-chip"
                      onClick={() => {
                        setShowRunPicker(false);
                        // 5+ off the bat almost always includes a boundary-overthrow;
                        // treat as a normal (non-boundary) runs ball so it routes
                        // through the wheel in detail mode like other run-scoring shots.
                        handleRun(n);
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="sc-modal-actions" style={{ marginTop: 14 }}>
                  <button className="sc-cancel" onClick={() => setShowRunPicker(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSub && (
          <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowSub(false)}>
            <div className="sc-modal">
              <div className="sc-modal-body">
                <h3>Substitute fielder</h3>
                <div style={{ fontSize: 12, color: 'var(--sc-text-mute, #888)', marginBottom: 10 }}>
                  A substitute fields only — they can take catches and run-outs but do not bat or bowl.
                  Pick the fielding side and the member coming on.
                </div>
                <h4>Fielding side</h4>
                <div className="sc-chip-row">
                  {['A', 'B'].map(s => (
                    <button
                      key={s}
                      className={`sc-chip ${subSide === s ? 'sc-active' : ''}`}
                      onClick={() => setSubSide(s)}
                    >
                      {s === 'A' ? (teamAName || 'Side A') : (teamBName || 'Side B')}
                    </button>
                  ))}
                </div>
                <h4>Substitute (member)</h4>
                <select
                  value={subMemberId}
                  onChange={(e) => setSubMemberId(e.target.value)}
                  style={{ width: '100%', padding: 8, marginBottom: 8 }}
                >
                  <option value="">— pick a member —</option>
                  {[...membersById.values()]
                    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
                    .map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                </select>
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={subCanKeep} onChange={(e) => setSubCanKeep(e.target.checked)} />
                  Allowed to keep wicket
                </label>
                <div className="sc-modal-actions" style={{ marginTop: 14 }}>
                  <button className="sc-cancel" onClick={() => setShowSub(false)}>Cancel</button>
                  <button
                    className="sc-confirm"
                    disabled={!subMemberId || !subSide}
                    onClick={async () => {
                      try {
                        await api.post(`/scorer/matches/${matchId}/substitute`, {
                          member_id: subMemberId,
                          side: subSide,
                          can_keep: subCanKeep,
                        });
                        setToast({ kind: 'ok', text: 'Substitute fielder added.' });
                        setShowSub(false);
                        setSubMemberId('');
                        refresh();
                      } catch (e) {
                        setToast({ kind: 'err', text: e?.response?.data?.error || 'Add substitute failed' });
                      }
                    }}
                  >
                    Add substitute
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showExtras && (
          <ExtrasModal
            kind={showExtras}
            onCancel={() => setShowExtras(null)}
            onConfirm={onExtraConfirm}
          />
        )}
        {editingBall && (() => {
          // Use the squad for the EDITED ball's innings (not necessarily the
          // current one) so the bowler/fielder options are correct.
          const eInn = (allInnings || []).find(i => i.id === editingBall.innings_id) || currentInnings;
          const eBowlSquad = (eInn?.bowling_side === 'A' ? squadA : squadB);
          const eBatSquad  = (eInn?.batting_side  === 'A' ? squadA : squadB);
          // Include substitute fielders on the edited ball's fielding side so a
          // sub can be credited/corrected as the dismissal fielder.
          const eFieldingSquad = [...eBowlSquad, ...(eInn?.bowling_side === 'A' ? subsA : subsB)];
          return (
            <EditBallModal
              key={editingBall.id}
              ball={editingBall}
              bowlingSquad={eFieldingSquad}
              battingSquad={eBatSquad}
              membersById={membersById}
              onCancel={() => setEditingBall(null)}
              onSave={onEditSave}
              busy={editBusy}
            />
          );
        })()}
        {showBallBrowser && (
          <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowBallBrowser(false)}>
            <div className="sc-modal" style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
              <div className="sc-modal-body" style={{ overflowY: 'auto' }}>
                <h3>Edit any ball</h3>
                <div style={{ fontSize: 11, color: 'var(--sc-text-mute)', marginTop: -8, marginBottom: 12 }}>
                  Tap a delivery to edit its runs, dismissal, or bowler. Figures re-derive on save.
                </div>
                {ballBrowserBusy && <div style={{ color: 'var(--sc-text-mute)' }}>Loading…</div>}
                {!ballBrowserBusy && allBalls && allBalls.length === 0 && (
                  <div style={{ color: 'var(--sc-text-mute)' }}>No balls recorded yet.</div>
                )}
                {!ballBrowserBusy && allBalls && allBalls.length > 0 && (() => {
                  const byInn = {};
                  allBalls.forEach(b => { (byInn[b.innings_id] = byInn[b.innings_id] || []).push(b); });
                  return Object.keys(byInn).map(innId => {
                    const innRow = (allInnings || []).find(i => i.id === innId);
                    const innNo = innRow?.innings_number ?? '?';
                    const byOver = {};
                    byInn[innId].forEach(b => { (byOver[b.over_number] = byOver[b.over_number] || []).push(b); });
                    const overs = Object.keys(byOver).map(Number).sort((a, b) => a - b);
                    return (
                      <div key={innId} style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 800, color: 'var(--sc-gold-mid)', margin: '8px 0' }}>Innings {innNo}</div>
                        {overs.map(o => (
                          <div key={o} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sc-text-mute)', marginBottom: 4 }}>
                              Over {o + 1}
                            </div>
                            {byOver[o].map(b => {
                              const bowlNm = membersById.get(b.bowler_id)?.display_name || (b.bowler_id || '').slice(0, 8);
                              const summary = b.dismissal_type ? `W (${String(b.dismissal_type).replace(/_/g, ' ')})` : `${b.total_runs}r`;
                              return (
                                <button
                                  key={b.id}
                                  onClick={() => openBallFromBrowser(b)}
                                  style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, alignItems: 'center',
                                           padding: '8px 10px', marginBottom: 4, border: '1px solid var(--sc-border-mid, #e2e8f0)',
                                           borderRadius: 8, background: '#fff', cursor: 'pointer' }}
                                >
                                  <span style={{ fontWeight: 700, minWidth: 42 }}>{b.display_label || `${b.over_number}.${b.ball_in_over}`}</span>
                                  <span style={{ fontSize: 12, color: '#64748b', minWidth: 90 }}>{bowlNm}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700 }}>{summary}</span>
                                  <span style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                    {b.commentary_text || ''}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="sc-modal-actions">
                <button className="sc-cancel" onClick={() => setShowBallBrowser(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
        {showInningsEnd && (
          <InningsEndModal
            inningsNumber={currentInnings?.innings_number}
            onCancel={() => setShowInningsEnd(false)}
            onConfirm={onInningsEndConfirm}
            busy={inningsEndBusy}
          />
        )}
        {showWicket && (
          <WicketModal
            striker={striker}
            nonStriker={nonStriker}
            bowlingSquad={fieldingSquad}
            battingSquad={battingSquad}
            alreadyOutMemberIds={alreadyOut}
            membersById={membersById}
            drsEnabled={!!state?.drs_config?.enabled}
            drsBatterChances={liveStateForCur?.striker_id ? derived?.drsByKey?.get(`${liveStateForCur.striker_id}|batter`) : null}
            onCancel={() => setShowWicket(false)}
            onConfirm={onWicketConfirm}
          />
        )}
        {showWheel && pendingDelivery && (
          <WagonWheelModal
            delivery={pendingDelivery}
            batterName={striker?.display_name || ''}
            bowlerName={bowler?.display_name || ''}
            bowlingSquad={fieldingSquad}
            membersById={membersById}
            onSkip={onWheelSkip}
            onConfirm={onWheelConfirm}
          />
        )}
        {showBowlerChange && (
          <BowlerChangeModal
            matchId={matchId}
            inningsId={currentInnings.id}
            overNumber={Math.floor((parseInt(currentInnings.legal_balls, 10) || 0) / 6)}
            bowlingSquad={bowlingSquad}
            membersById={membersById}
            previousBowlerId={liveStateForCur?.bowler_id}
            bannedBowlerIds={state?.banned_bowlers || []}
            ballsThisOver={ballsThisOver.length}
            legalThisOver={ballsThisOver.filter(b => b.is_legal).length}
            overBowlerId={overBowlerId}
            lastOverBowlerId={lastOverBowlerId}
            forced={bowlerChangeForced}
            limit={curLimit ? curLimit.limit : null}
            oversBy={oversBy}
            conditionsById={conditionsById}
            onCancel={() => { setShowBowlerChange(false); setBowlerChangeForced(false); }}
            onChanged={() => { setShowBowlerChange(false); setBowlerChangeForced(false); refresh(); }}
          />
        )}
        {showKeeper && (
          <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowKeeper(false)}>
            <div className="sc-modal">
              <div className="sc-modal-body">
                <h3>Change wicketkeeper</h3>
                <div style={{ fontSize: 12, color: 'var(--sc-text-mute)', marginBottom: 10 }}>
                  Fielding side ({bowlingSide}). The new keeper applies from the next ball onward — balls already
                  scored keep the keeper they were recorded with, so caught-behind/stumping credit stays correct.
                </div>
                <div className="sc-chip-row">
                  {bowlingSquad.map(s => {
                    const mem = membersById.get(s.member_id);
                    const avoids = !!mem?.avoids_wicketkeeping;
                    return (
                      <button
                        key={s.member_id}
                        className={`sc-chip ${s.is_keeper ? 'sc-active' : ''}`}
                        disabled={avoids}
                        title={avoids ? "Member has asked not to keep wickets" : ''}
                        onClick={async () => {
                          if (!(await confirmCondition({ matchId, memberId: s.member_id, kind: 'keeping', name: mem?.display_name,
                            inningsId: currentInnings?.id, overNumber: overNow, conditionsById }))) return;
                          try {
                            await api.post(`/scorer/matches/${matchId}/keeper`, {
                              side: bowlingSide,
                              member_id: s.member_id,
                            });
                            setShowKeeper(false);
                            await refresh();
                            setToast({ kind: 'ok', text: `Keeper: ${mem?.display_name || ''}` });
                          } catch (e) {
                            setToast({ kind: 'err', text: e?.response?.data?.error || 'Keeper change failed' });
                          }
                        }}
                      >
                        {mem?.display_name || s.member_id.slice(0, 8)}{conditionsById.get(s.member_id)?.no_keeping ? ' ⚠' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sc-modal-actions">
                <button className="sc-cancel" onClick={() => setShowKeeper(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="sc-root">
      <ScoringHeader
        match={setup?.match}
        currentInnings={currentInnings}
        teamAName={teamAName}
        teamBName={teamBName}
      />
      {body}
      {toast && (
        <div className={`sc-toast ${toast.kind === 'ok' ? 'sc-toast-ok' : ''}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}
